import axios from "axios";
import { cssNumber } from "jquery";
import Config from "services/config/config";
import {
  getSession,
  getLocalLogin,
  getRefreshLogin,
  getAsync,
  setLocalLogin,
  setAuthenticate,
  setRefreshToken,
  logoutUser,
} from "services/utils/storages/storages";
import { SHOW_HIDE_TOAST } from "services/redux/sales/appNotifications/type";
import { store } from "services/redux/store";

let isRefreshing = false;
let failedQueue = [];

const SESSION_EXPIRED = "Session expired!";

const isTokenExpiredV2Response = (data) =>
  data && Number(data.status) === 1 && String(data.message).toUpperCase() === "TOKEN_EXPIRED";

const extractRefreshTokens = (data) => {
  const payload = data?.response?.data ?? data?.data ?? {};
  return {
    token: payload?.token || null,
    refreshToken: payload?.refresh_token || null,
  };
};

const isRefreshSuccess = (data) =>
  Number(data?.status) === 0 || data?.status === true || data?.response?.status === true;

const forceSessionExpired = async (error = null) => {
  isRefreshing = false;
  processQueue(error || new Error(SESSION_EXPIRED), null);
  showToast(SESSION_EXPIRED);
  await logoutUser();
  window.location.reload();
  return Promise.reject(error || new Error(SESSION_EXPIRED));
};

// Allowlist: ONLY our own fusion backends use the internal API envelope, so only
// their responses are ever reshaped. Every third-party API (maps, vehicle,
// fastag, storage, recon, …) is left untouched by default. This is safer than a
// third-party denylist, which would reshape any external host we forgot to list.
const FUSION_API_HOSTS = [
  process.env.REACT_APP_GO_API_URL, // fusion-backend-go (V2 flat envelope)
  process.env.REACT_APP_API_URL, // fusion-backend (Laravel)
  process.env.REACT_APP_FUSION_SERVICE_URL, // fusion-service
]
  .filter((host) => typeof host === "string" && host.trim() !== "")
  .map((host) => host.trim());

// True only for requests to our fusion backends. Relative URLs (no explicit
// origin) target our own app/backend, so they count as fusion too.
const isFusionApiUrl = (url = "") => {
  if (typeof url !== "string" || url.trim() === "") return false;
  const isAbsolute = /^https?:\/\//i.test(url);
  if (!isAbsolute) return true;
  return FUSION_API_HOSTS.some((host) => url.includes(host));
};

const FUSION_SERVICE_PACKAGE_NAME =
  typeof process.env.REACT_APP_PACKAGE_NAME === "string" &&
  process.env.REACT_APP_PACKAGE_NAME.trim() !== ""
    ? process.env.REACT_APP_PACKAGE_NAME.trim()
    : "web.fusion.parkplus.io";

const isFusionServiceUrl = (url = "") => {
  if (typeof url !== "string" || url.trim() === "") return false;
  const fusionServiceHost = process.env.REACT_APP_FUSION_SERVICE_URL?.trim();
  if (fusionServiceHost && url.includes(fusionServiceHost)) return true;
  return url.includes("fusion-service");
};

const applyFusionServiceHeaders = (headers) => {
  headers["package-name"] = FUSION_SERVICE_PACKAGE_NAME;
  headers.platform = "web";
};

/**
 * Bridges the fusion-backend-go flat envelope `{ status:number, message, data }`
 * to the legacy `{ status_code, status:bool, response:{ status:bool, message, data } }`
 * shape that most of the app reads. This is ADDITIVE only: the original numeric
 * `status`, `data` and `message` are left untouched so already-migrated readers
 * (`res.data.status === 0`, `res.data.data`) keep working, while legacy readers
 * (`res.data.response.data`, `res.data.status_code`) start working too.
 * Convention: status 0 = success, non-zero = error.
 */
const normalizeApiEnvelope = (url = "", body) => {
  try {
    // Only reshape responses from our fusion backends; never third parties.
    if (!isFusionApiUrl(url)) return body;
    if (!body || typeof body !== "object" || Array.isArray(body)) return body;
    // Already legacy-shaped (has a `response` wrapper) → leave as-is.
    if (Object.prototype.hasOwnProperty.call(body, "response")) return body;
    // Only the flat v2 envelope qualifies: numeric `status` + a `data` payload.
    const hasNumericStatus = typeof body.status === "number";
    const hasData = Object.prototype.hasOwnProperty.call(body, "data");
    if (!hasNumericStatus || !hasData) return body;

    const isSuccess = Number(body.status) === 0;
    body.response = {
      status: isSuccess,
      message: body.message ?? "",
      data: body.data,
    };
    if (!Object.prototype.hasOwnProperty.call(body, "status_code")) {
      body.status_code = isSuccess ? 200 : 400;
    }
    return body;
  } catch (e) {
    return body;
  }
};

const refreshAndRetry = async (originalRequest) => {
  // if token refreshing mechanism is finished then reset all queued api's
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((token) => {
        originalRequest.headers["Authorization"] = `${token}`;
        return axios(originalRequest);
      })
      .catch((err) => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  const refreshToken = await getRefreshLogin();
  if (!refreshToken) {
    return forceSessionExpired();
  }

  let loginDetails = null;
  try {
    const rawLoginDetails = await getAsync("loginDetails");
    loginDetails = rawLoginDetails ? JSON.parse(rawLoginDetails) : null;
  } catch (e) {
    loginDetails = null;
  }

  if (!loginDetails?.id) {
    return forceSessionExpired();
  }

  const fusionToken = localStorage.getItem("fusion-token");

  try {
    const { data } = await axios.post(
      Config.loginApiUrl.refreshLogin,
      {
        refresh_token: refreshToken,
        user_id: loginDetails.id,
        source: "fusion",
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${fusionToken}`,
        },
      }
    );

    const { token: newToken, refreshToken: newRefreshToken } = extractRefreshTokens(data);

    // Expired/invalid refresh token, validation errors returned as HTTP 200, etc.
    if (!isRefreshSuccess(data) || !newToken) {
      return forceSessionExpired();
    }

    if (newRefreshToken) {
      setRefreshToken(newRefreshToken);
    }

    updateRefreshTokenInNative(newToken);
    originalRequest.headers["Authorization"] = `${newToken}`;
    await setLocalLogin(newToken);
    await setAuthenticate(newToken);
    processQueue(null, newToken);
    return axios(originalRequest);
  } catch (err) {
    // HTTP 4xx/5xx from retry-login (e.g. Unauthorized / validation) → hard logout
    return forceSessionExpired(err);
  } finally {
    isRefreshing = false;
  }
};

// Helper function to process the failed requests queue
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const showToast = (errorMsg) => {
  store.dispatch({
    type: SHOW_HIDE_TOAST,
    payload: { isToast: true, toastMessage: errorMsg },
  });
};

const isReconUrl = (url = "") => {
  const value = String(url || "");
  return [
    Config.installation.handoverValetContractPreview,
    Config.brandActivation.invoiceList,
    Config.brandActivation.addPayment,
  ].some((endpoint) => endpoint && value.includes(endpoint));
};

axios.interceptors.request.use(
  async (evt) => {
    if (checkTokenNeed(evt.url)) {
      if (isReconUrl(evt.url)) {
        evt.headers[`client-secret`] = `${process.env.REACT_APP_CLIENT_SECRET}`;
        evt.headers[`client-id`] = `${process.env.REACT_APP_CLIENT_ID}`;
        if (evt.url.includes("handover-valet-contract-preview")) {
          const token = await getLocalLogin();
          if (token) {
            evt.headers.Authorization = `${token}`;
          }
        }
        return evt;
      }
      const token = await getLocalLogin();
      if (evt.url.includes("storage.parkplus")) {
        evt.headers[`client-secret`] = `${process.env.REACT_APP_STORAGE_CLIENT_SECRET}`;
        evt.headers[`client-id`] = `${process.env.REACT_APP_STORAGE_CLIENT_ID}`;
        if (token) {
          evt.headers.Authorization = `${token}`;
        }
        return evt;
      }
      // const refreshToken = await getRefreshLogin();
      // if(!token || !refreshToken){
      //     showToast(SESSION_EXPIRED);
      //     await logoutUser();
      //     window.location.reload();
      // }
      // console.log(evt.url);
      if (
        token &&
        (evt.url.includes("vehicle") ||
          evt.url.includes("fastag-issuance") ||
          evt.url.includes("user-service") ||
          isFusionServiceUrl(evt.url))
      ) {
        evt.headers.Authorization = `${token}`;
        evt.headers[
          `client-secret`
        ] = `${process.env.REACT_APP_VEHICLE_CLIENT_SECRET}`;
        evt.headers[`client-id`] = `${process.env.REACT_APP_VEHICLE_CLIENT_ID}`;
        if (isFusionServiceUrl(evt.url)) {
          applyFusionServiceHeaders(evt.headers);
        }
        return evt;
      }
      if (token && evt.url.includes(Config.siteLeadReferral.apiPath)) {
        evt.headers.Authorization = `${token}`;
        if (evt.method?.toLowerCase() === 'patch') {
          try {
            const profile = JSON.parse(localStorage.getItem('profileDetails') || '[]');
            const row = Array.isArray(profile) ? profile[0] : profile;
            const userId = row?.user_id ?? row?.id;
            if (userId != null) {
              evt.headers['user-service-id'] = String(userId);
            }
          } catch {
            // profile parse failed — skip ops actor header
          }
        }
        return evt;
      }
      if (token && !isReconUrl(evt.url)) {
        evt.headers.Authorization = `${token}`;
        return evt;
      }
    }
    return evt;
  },
  (err) => Promise.reject(err)
);

axios.interceptors.response.use(
  async (evt) => {
    const originalRequest = evt.config;
    if (checkTokenNeed(evt?.config?.url)) {
      if (isReconUrl(evt.config.url)) {
        return evt;
      }
      // if(evt?.data?.status_code == 401){
      //     await logoutUser();
      //     window.location.reload();
      // }
      // incase of token expired
      if (
        (evt?.data?.status_code == 401 || isTokenExpiredV2Response(evt?.data)) &&
        !originalRequest._retry
      ) {
        return refreshAndRetry(originalRequest);
      }
    }

    evt.data = normalizeApiEnvelope(evt?.config?.url, evt.data);
    return evt;
  },
  async (err) => {
    const originalRequest = err?.config;
    const data = err?.response?.data;
    const httpStatus = err?.response?.status;

    if (
      originalRequest &&
      checkTokenNeed(originalRequest?.url) &&
      !originalRequest._retry &&
      (httpStatus === 401 || isTokenExpiredV2Response(data) || data?.status_code == 401)
    ) {
      return refreshAndRetry(originalRequest);
    }

    if (err?.response?.data) {
      err.response.data = normalizeApiEnvelope(originalRequest?.url, err.response.data);
    }
    return Promise.reject(err);
  }
);

const updateRefreshTokenInNative = (token) => {
  let webView = window.localStorage.isWebview;
  // delete window.localStorage.clear()
  // window.location.href="/"
  if (webView !== undefined && webView == "true") {
    console.log("update token event fired", token);
    const message = {
      event_name: "update_token",
      page: "interceptor.js",
      accessToken: token,
      roleId: localStorage.getItem("role_id"),
      refreshToken: localStorage.getItem("fusion-refresh-token"),
      loginDetails: localStorage.getItem("loginDetails"),
    };
    const nativeObject = window.ReactNativeWebView;
    if (nativeObject !== undefined) {
      nativeObject.postMessage(JSON.stringify(message));
      window.addEventListener("message", () => {});
    }
  }
};

const checkTokenNeed = (url) =>
    url.includes(Config.profileUrl.profile) ||
    url.includes(Config.LeadDetails.source) ||
    url.includes(Config.pocDetials.type) ||
    url.includes(Config.categoryDetials.lead) ||
    url.includes(Config.locations.city) ||
    url.includes(Config.locations.zone) ||
    url.includes(Config.locations.state) ||
    url.includes(Config.locations.area) ||
    url.includes(Config.locations.locationType) ||
    url.includes(Config.poc.addPoc) ||
    url.includes(Config.poc.getPoc) ||
    url.includes(Config.gates.gateTypeList) ||
    url.includes(Config.gates.fetchLeadGate) ||
    url.includes(Config.gates.addGate) ||
    url.includes(Config.preB2bAlliance.allianceDropdown) ||
    url.includes(Config.createLead.sales) ||
    url.includes(Config.postB2bAlliance.dropdownValues) ||
    url.includes(Config.gates.addCorporateGate) ||
    url.includes(Config.gates.addApartmentGate) ||
    url.includes(Config.preB2bAlliance.addPreAlliance) ||
    url.includes(Config.postB2bAlliance.addPostAlliance) ||
    url.includes(Config.locations.pinCode) ||
    url.includes(Config.createLead.corporate) ||
    url.includes(Config.categoryDetials.supplyName) ||
    url.includes(Config.createLead.apartment) ||
    url.includes(Config.preSalesList.type) ||
    url.includes(Config.preSalesList.status) ||
    url.includes(Config.preSalesList.listApartments) ||
    url.includes(Config.maintenance.sendInspectionForm) ||
    url.includes(Config.maintenance.sendInspectionMail) ||
    url.includes(Config.maintenance.setInspectionForm) ||
    url.includes(Config.maintenance.getInspectionForm) ||
    url.includes(Config.maintenance.viewAssigneeData) ||
    url.includes(Config.preSalesList.fetchAllData) ||
    url.includes(Config.preSalesList.listAlliance) ||
    url.includes(Config.postSalesList.createSiteInspectionForm) ||
    url.includes(Config.postSalesList.inspectionCreated) ||
    url.includes(Config.postSalesList.viewSiteInspectionForms) ||
    url.includes(Config.postSalesList.listSiteInspectionManagers) ||
    url.includes(Config.postSalesList.changeSiteInspectionManager) ||
    url.includes(Config.preSalesList.listSupply) ||
    url.includes(Config.followUpList.list) ||
    url.includes(Config.viewPoc.list) ||
    url.includes(Config.viewPoc.status) ||
    url.includes(Config.viewPoc.bastatus) ||
    url.includes(Config.preSalesList.updateStatus) ||
    url.includes(Config.preSalesSupply.dropDownList) ||
    url.includes(Config.categoryDetials.rwaTenure) ||
    url.includes(Config.createLead.b2bAlliance) ||
    url.includes(Config.createLead.supply) ||
    url.includes(Config.postSales.supply) ||
    url.includes(Config.postSales.corporate) ||
    url.includes(Config.postSales.appartment) ||
    url.includes(Config.postSales.alliances) ||
    url.includes(Config.postSales.createPostCorporate) ||
    url.includes(Config.postSales.createPostApartment) ||
    url.includes(Config.postSales.corporateContract) ||
    url.includes(Config.postSales.appartmentContract) ||
    url.includes(Config.postSales.createPostSupply) ||
    url.includes(Config.poc.getPocById) ||
    url.includes(Config.postSalesList.type) ||
    url.includes(Config.preSalesSupply.create) ||
    url.includes(Config.poc.getPocById) ||
    url.includes(Config.poc.editPoc) ||
    url.includes(Config.approvals.accept) ||
    url.includes(Config.approvals.reject) ||
    url.includes(Config.followUp.dropDown) ||
    url.includes(Config.followUp.saveForm) ||
    url.includes(Config.poc.getPocById) ||
    url.includes(Config.poc.editPoc) ||
    url.includes(Config.pocDetials.siteType) ||
    url.includes(Config.pocDetials.addClient) ||
    url.includes(Config.pocDetials.getAllClients) ||
    url.includes(Config.pocDetials.getAddressClient) ||
    url.includes(Config.pocDetials.setAddressClient) ||
    url.includes(Config.pocDetials.getClientBusinessDetails) ||
    url.includes(Config.pocDetials.getAddressBusiness) ||
    url.includes(Config.pocDetials.setAddressBusiness) ||
    url.includes(Config.viewDetails.view) ||
    url.includes(Config.cxOrderRefundList) ||
    url.includes(Config.cxOrderData) ||
    url.includes(Config.refundApproval) ||
    url.includes(Config.viewDetails.viewContract) ||
    url.includes(Config.categoryDetials.supplyDropDown) ||
    url.includes(Config.followUp.followUpDetails) ||
    url.includes(Config.categoryDetials.corporateDropDown) ||
    url.includes(Config.categoryDetials.appartmentDropDown) ||
    url.includes(Config.assignee.dropdownData) ||
    url.includes(Config.listFilterData.fillterDropdown) ||
    url.includes(Config.listFilterData.fillterVendorDropdown) ||
    url.includes(Config.listFilterData.fetchZone) ||
    url.includes(Config.assignee.postAssignee) ||
    url.includes(Config.postSales.getMaterial) ||
    url.includes(Config.postSales.getBrand) ||
    url.includes(Config.materialRequistionForm.getCategoryData) ||
    url.includes(Config.postSales.getSolution) ||
    url.includes(Config.postSales.getTag) ||
    url.includes(Config.postSales.getRevenueNature) ||
    url.includes(Config.postSales.checkManagementFeeApplicable) ||
    url.includes(Config.createLead.b2bDropdown) ||
    url.includes(Config.createLead.projectOnboarding) ||
    url.includes(Config.createLead.updateprojectOnboarding) ||
    url.includes(Config.fileUpload.imageUpload) ||
    url.includes(Config.fileUpload.imageUploadPublic) ||
    url.includes(Config.fileUpload.mediaUpload) ||
    url.includes(Config.fileUpload.docUpload) ||
    url.includes(Config.fileUpload.documentUploadForm) ||
    url.includes(Config.fileUpload.excelUpload) ||
    url.includes(Config.installation.siteListing) ||
    url.includes(Config.installation.siteStatusCSV) ||
    url.includes(Config.installation.taskListing) ||
    url.includes(Config.installation.meetingList) ||
    url.includes(Config.installation.createMeeting) ||
    url.includes(Config.viewDetails.getLeadType) ||
    url.includes(Config.installation.pocList) ||
    url.includes(Config.installation.getSiteLog) ||
    url.includes(Config.installation.firAutofill) ||
    url.includes(Config.installation.corporateAutofillData) ||
    url.includes(Config.installation.targetData) ||
    url.includes(Config.installation.postTargetData) ||
    url.includes(Config.installation.postVerifyData) ||
    url.includes(Config.installation.siteStage) ||
    url.includes(Config.installation.siteStageStatus) ||
    url.includes(Config.installation.siteUnhold) ||
    url.includes(Config.installation.siteDismantle) ||
    url.includes(Config.installation.siteLERT) ||
    url.includes(Config.installation.siteLertApproval) ||
    url.includes(Config.installation.siteInspectionStage) ||
    url.includes(Config.installation.siteInspectionStageSolution) ||
    url.includes(Config.installation.postSolution) ||
    url.includes(Config.importLead) ||
    url.includes(Config.uploadFlat) ||
    url.includes(Config.brandActivation.giveRating) ||
    url.includes(Config.brandActivation.getRating) ||
    url.includes(Config.editLead.corporate) ||
    url.includes(Config.editLead.apartment) ||
    url.includes(Config.editLead.b2bAlliance) ||
    url.includes(Config.editLead.supply) ||
    url.includes(Config.installation.inventoryCheckList) ||
    url.includes(Config.installation.meetingById) ||
    url.includes(Config.installation.cancelMeeting) ||
    url.includes(Config.installation.completeMeeting) ||
    url.includes(Config.installation.siteInspectionStage) ||
    url.includes(Config.notifications.fetchUnread) ||
    url.includes(Config.notifications.fetchAll) ||
    url.includes(Config.installation.siteInformationChecklist) ||
    url.includes(Config.installation.civilCheckList) ||
    url.includes(Config.installation.hardwareInstall) ||
    url.includes(Config.installation.handOver) ||
    url.includes(Config.installation.assigneeData) ||
    url.includes(Config.installation.siteAssign) ||
    url.includes(Config.installation.vendorList) ||
    url.includes(Config.installation.vendorAllocate) ||
    url.includes(Config.installation.vendorAllocate) ||
    url.includes(Config.installation.vendorServiceDetail) ||
    url.includes(Config.installation.saveSiteInformation) ||
    url.includes(Config.installation.saveCivilCheckList) ||
    url.includes(Config.installation.saveHardwareInstall) ||
    url.includes(Config.installation.saveHandOver) ||
    url.includes(Config.installation.gateInfo) ||
    url.includes(Config.installation.gateDetail) ||
    url.includes(Config.installation.postVendorService) ||
    url.includes(Config.approvals.locApprovalList) ||
    url.includes(Config.approvals.locApprovalAccept) ||
    url.includes(Config.approvals.locApprovalReject) ||
    url.includes(Config.installation.gateInventory) ||
    url.includes(Config.installation.mrfDetail) ||
    url.includes(Config.installation.addReportDetails) ||
    url.includes(Config.installation.siteLocationApproval) ||
    url.includes(Config.installation.siteLocationApproval) ||
    url.includes(Config.installation.siteEngineerList) ||
    url.includes(Config.installation.siteEngineerDetails) ||
    url.includes(Config.installation.siteRepository) ||
    url.includes(Config.installation.getSiteRepository) ||
    url.includes(Config.attendance.attendanceList) ||
    url.includes(Config.attendance.fetchAttd) ||
    url.includes(Config.attendance.attendanceOfficeList) ||
    url.includes(Config.attendance.attendanceSiteList) ||
    url.includes(Config.attendance.storeEmployeeAttendanceData) ||
    url.includes(Config.installation.sendApproval) ||
    url.includes(Config.installation.siteEngineerDetails) ||
    url.includes(Config.approvals.mrfApprovalList) ||
    url.includes(Config.approvals.mrfApprovalAccept) ||
    url.includes(Config.approvals.mrfApprovalReject) ||
    url.includes(Config.installation.getSiteAbortApproval) ||
    url.includes(Config.installation.getMaterialDelivery) ||
    url.includes(Config.installation.getTagDelivery) ||
    url.includes(Config.installation.getTags) ||
    url.includes(Config.installation.postTagDelivery) ||
    url.includes(Config.installation.postMaterialDelivery) ||
    url.includes(Config.installation.statusUpdate) ||
    url.includes(Config.installation.generateReport) ||
    url.includes(Config.installation.handoverValetContractPreview) ||
    url.includes(Config.installation.sendtoProcurement) ||
    url.includes(Config.installation.siteEngineerDetails) ||
    url.includes(Config.tickets.getTicketsList) ||
    url.includes(Config.tickets.getTicketDetails) ||
    url.includes(Config.tickets.getSearchUser) ||
    url.includes(Config.tickets.replyOnTicket) ||
    url.includes(Config.tickets.createTicket) ||
    url.includes(Config.installation.locationApprovalPending) ||
    url.includes(Config.installation.siteEngineerDetails) ||
    url.includes(Config.tickets.statusList) ||
    url.includes(Config.installation.pocListBySite) ||
    url.includes(Config.installation.sendReportMail) ||
    url.includes(Config.tickets.updateStatus) ||
    url.includes(Config.maintenance.fetchGoLiveSites) ||
    url.includes(Config.maintenance.fetchGatesMaintenanceDetails) ||
    url.includes(Config.maintenance.fetchGatesMaintenanceMaterials) ||
    url.includes(Config.maintenance.fetchMaintenanceCategoryType) ||
    url.includes(Config.maintenance.changeSiteStateToEnableOrDisable) ||
    url.includes(Config.maintenance.getSiteStateToEnableOrDisable) ||
    url.includes(Config.maintenance.getPmSiteStateList) ||
    url.includes(Config.maintenance.updatePmSiteState) ||
    url.includes(Config.maintenance.fetchMaintenanceCategory) ||
    url.includes(Config.maintenance.fetchMaintenanceMaterialList) ||
    url.includes(Config.maintenance.postPmData) ||
    url.includes(Config.maintenance.fetchMaintenancePMSites) ||
    url.includes(Config.maintenance.pmChecklist) ||
    url.includes(Config.maintenance.postPmAnswer) ||
    url.includes(Config.maintenance.fetchGatesMaintenanceMaterials) ||
    url.includes(Config.fileComplain.siteTypes) ||
    url.includes(Config.fileComplain.siteSearch) ||
    url.includes(Config.sitesLocMap.getSitesLocMap) ||
    url.includes(Config.maintenance.fetchMaintenanceMonths) ||
    url.includes(Config.maintenance.downloadPMList) ||
    url.includes(Config.maintenance.uploadPMList) ||
    url.includes(Config.fileComplain.dropdownsData) ||
    url.includes(Config.fileComplain.complainRaised) ||
    url.includes(Config.fileComplain.fetchFaqs) ||  
    url.includes(Config.fileComplain.fetchTicketCategory) || 
    url.includes(Config.fileComplain.updateComplainCategory) ||  
    url.includes(Config.sitesLocMap.getSitesLocMap) ||
    url.includes(Config.employeeTickets.createTicket) ||
    url.includes(Config.fileComplain.fetchComplain) ||
    url.includes(Config.fileComplain.fetchAllIssue) ||
    url.includes(Config.fileComplain.complainRaisedTicket) ||
    url.includes(Config.fileComplain.saveComplaintReply) ||
    url.includes(Config.fileComplain.saveAssigneeTicket) ||
    url.includes(Config.fileComplain.closeComplain) ||
    url.includes(Config.fileComplain.reopenComplain) ||
    url.includes(Config.maintenance.fetchPMTickets) ||
    url.includes(Config.maintenance.PMTicketDetails) ||
    url.includes(Config.maintenance.fetchUserAssignee) ||
    url.includes(Config.maintenance.storeSchedulePM) ||
    url.includes(Config.maintenance.storeScheduleProjectPM)||
    url.includes(Config.maintenance.pmticketUpdateStatus) ||
    url.includes(Config.maintenance.postRemarks) ||
    url.includes(Config.employeeTickets.createTicket) ||
    url.includes(Config.maintenance.getSiteGates) ||
    url.includes(Config.maintenance.ticketAssignment) ||
    url.includes(Config.fileComplain.viewComaplain) ||
    url.includes(Config.fileComplain.getSubIssueDropDown) ||
    url.includes(Config.fileComplain.duplicateComplainData) ||
    url.includes(Config.fileComplain.saveComplaintReply) ||
    url.includes(Config.fileComplain.closeDuplicateComplaint) ||
    url.includes(Config.maintenance.approvePmSite) ||
    url.includes(Config.maintenance.rejectPmSite) ||
    url.includes(Config.maintenance.pmSendApproval) ||
    url.includes(Config.maintenance.pmStatus) ||
    url.includes(Config.maintenance.pmTicketStatus) ||
    url.includes(Config.maintenance.pmTicketAssignee) ||
    url.includes(Config.maintenance.rmList) ||
    url.includes(Config.maintenance.rmSoftwareList) ||
    url.includes(Config.maintenance.rmHardwareList) ||
    url.includes(Config.maintenance.rmListReport) ||
    url.includes(Config.maintenance.rmTicketLog) ||
    url.includes(Config.maintenance.viewRmList) ||
    url.includes(Config.installation.siteFilters) ||
    url.includes(Config.maintenance.rmUpdateStatus) ||
    url.includes(Config.maintenance.rmAssignment) ||
    url.includes(Config.maintenance.employeeTicketsList) ||
    url.includes(Config.maintenance.viewEmployeeTickets) ||
    url.includes(Config.maintenance.emUpdateStatus) ||
    url.includes(Config.maintenance.emAssignment) ||
    url.includes(Config.maintenance.postEmployeeRemark) ||
    url.includes(Config.maintenance.postRmRemark) ||
    url.includes(Config.maintenance.getRMMaterial) ||
    url.includes(Config.maintenance.getEquipment) ||
    url.includes(Config.maintenance.getComponants) ||
    url.includes(Config.maintenance.getLevels) ||
    url.includes(Config.maintenance.getLevelsDropdown) ||
    url.includes(Config.maintenance.rmStatus) ||
    url.includes(Config.maintenance.emStatus) ||
    url.includes(Config.maintenance.pmSiteViewReport) ||
    url.includes(Config.fileComplain.getTroubleshoot) ||
    url.includes(Config.fileComplain.postTroubleshoot) ||
    url.includes(Config.maintenance.pmSiteReport) ||
    url.includes(Config.maintenance.pmSiteReportDownload) ||
    // url.includes(Config.maintenance.createMrfForMaintenance) ||
    url.includes(Config.maintenance.siteSchedule) ||
    url.includes(Config.maintenance.scheduledTicket) ||
    url.includes(Config.maintenance.engineerList) ||
    url.includes(Config.maintenance.pmSoftwareAnswer) ||
    url.includes(Config.maintenance.pmSoftwareSites) ||
    url.includes(Config.maintenance.pmSoftwareUpdateAssignee) ||
    url.includes(Config.maintenance.pmSoftwareViewReport) ||
    url.includes(Config.maintenance.pmSoftwareRejected) ||
    url.includes(Config.maintenance.pmSoftwareApproved) ||
    url.includes(Config.maintenance.pmSoftwarePostMail) ||
    url.includes(Config.maintenance.pmSoftwarePmDownload) ||
    url.includes(Config.maintenance.pmSoftwareFilter) ||
    url.includes(Config.brandActivation.getCSV) ||
    url.includes(Config.brandActivation.getLateFee) ||
    url.includes(Config.brandActivation.setInstallmentFee) ||
    url.includes(Config.brandActivation.dashboardSiteListing) ||
    url.includes(Config.brandActivation.dashboardInvoiceListing) ||
    url.includes(Config.brandActivation.dashboardSetInvoiceData) ||
    url.includes(Config.brandActivation.sitesListing) ||
    url.includes(Config.brandActivation.siteMaintenance) ||
    url.includes(Config.brandActivation.siteFilters) ||
    url.includes(Config.brandActivation.sitePocList) ||
    url.includes(Config.brandActivation.viewdetails) ||
    url.includes(Config.brandActivation.sitePocAdd) ||
    url.includes(Config.brandActivation.sitePocUpdate) ||
    url.includes(Config.brandActivation.meetingList) ||
    url.includes(Config.brandActivation.meetingLists) ||
    url.includes(Config.brandActivation.usersListForHod) ||
    url.includes(Config.brandActivation.saveUserRatingByHod) ||
    url.includes(Config.brandActivation.getUserDataByUserId) ||
    url.includes(Config.brandActivation.getJuniorListForImmediateSenior) ||
    url.includes(Config.brandActivation.saveMeetingRatingBySenior) ||
    url.includes(Config.brandActivation.selfScorecardByHod) ||
    url.includes(Config.brandActivation.selfScorecardBySenior) ||
    url.includes(Config.brandActivation.emailDropdownForRating) ||
    url.includes(Config.brandActivation.getFeedback) ||
    url.includes(Config.brandActivation.createMeeting) ||
    url.includes(Config.brandActivation.saveMeetingAsDraft) ||
    url.includes(Config.brandActivation.saveMeetingCheckIn) ||
    url.includes(Config.brandActivation.meetingDraftList) ||
    url.includes(Config.brandActivation.getPocDropdown) ||
    url.includes(Config.brandActivation.discardMeetingDraft) ||
    url.includes(Config.brandActivation.meetingById) ||
    url.includes(Config.brandActivation.createCampagin) ||
    url.includes(Config.brandActivation.campaginData) ||
    url.includes(Config.brandActivation.updateCampaginStatus) ||
    url.includes(Config.brandActivation.invoiceCreationCampaign) ||
    url.includes(Config.brandActivation.feedbackById) ||
    url.includes(Config.brandActivation.meetingPoc) ||
    url.includes(Config.brandActivation.cancelMeeting) ||
    url.includes(Config.brandActivation.completeMeeting) ||
    url.includes(Config.brandActivation.meetingMomAdd) ||
    url.includes(Config.brandActivation.updateMeeting) ||
    url.includes(Config.brandActivation.meetingMomView) ||
    url.includes(Config.brandActivation.conductCampListing) ||
    url.includes(Config.brandActivation.createCamp) ||
    url.includes(Config.brandActivation.viewCamp) ||
    url.includes(Config.brandActivation.campMom) ||
    url.includes(Config.brandActivation.campMomView) ||
    url.includes(Config.products.fetchProductsList) ||
    url.includes(Config.products.fetchProduct) ||
    url.includes(Config.brandActivation.getVendor) ||
    url.includes(Config.brandActivation.postVendor) ||
    url.includes(Config.brandActivation.viewVendor) ||
    url.includes(Config.products.checkoutOrder) ||
    url.includes(Config.products.sitesList) ||
    url.includes(Config.brandActivation.addCorporateClient) ||
    url.includes(Config.brandActivation.getCorporateClient) ||
    url.includes(Config.brandActivation.corporateClientPocList) ||
    url.includes(Config.brandActivation.addcorporateClientPoc) ||
    url.includes(Config.brandActivation.invoiceList) ||
    url.includes(Config.brandActivation.addPayment) ||
    url.includes(Config.knowledgeCenter.fetchKnowledgeCenter) ||
    url.includes(Config.brandActivation.deleteClient) ||
    url.includes(Config.brandActivation.updateClient) ||
    url.includes(Config.knowledgeCenter.fetchKnowledgeCenter) ||
    url.includes(Config.brandActivation.clientPocAdd) ||
    url.includes(Config.brandActivation.clientPocUpdate) ||
    url.includes(Config.brandActivation.getClientPocById) ||
    url.includes(Config.brandActivation.fetchPreCampQues) ||
    url.includes(Config.preSalesList.assignes) ||
    url.includes(Config.preSalesList.approvalList) ||
    url.includes(Config.preSalesList.requestListing) ||
    url.includes(Config.preSalesList.financeContractList) ||
    url.includes(Config.preSalesList.getSites) ||
    url.includes(Config.preSalesList.getSiteDetail) ||
    url.includes(Config.preSalesList.exportSitesCsv) ||
    url.includes(Config.preSalesList.paymentBusiness) ||
    url.includes(Config.preSalesList.paymentRequest) ||
    url.includes(Config.preSalesList.performaInvoiceListing) ||
    url.includes(Config.preSalesList.taxInvoiceListing) ||
    url.includes(Config.preSalesList.addAdvance) ||
    url.includes(Config.preSalesList.addAdvanceUnclaim) ||
    url.includes(Config.preSalesList.addPayment) ||
    url.includes(Config.preSalesList.adjustAdvance) ||
    url.includes(Config.preSalesList.vendorRequestList) ||
    url.includes(Config.preSalesList.vendorOnboardedList) ||
    url.includes(Config.preSalesList.approveVendor) ||
    url.includes(Config.preSalesList.getVendorBankDetailFields) ||
    url.includes(Config.preSalesList.fetchGeoCity) ||
    url.includes(Config.preSalesList.fetchGeoPincode) ||
    url.includes(Config.preSalesList.addVendorBankDetail) ||
    url.includes(Config.preSalesList.getVendorAddressFields) ||
    url.includes(Config.preSalesList.addVendorAddress) ||
    url.includes(Config.preSalesList.editVendorAddress) ||
    url.includes(Config.preSalesList.addVendorRequest) ||
    url.includes(Config.preSalesList.getPaymentRequestDetails) ||
    url.includes(Config.preSalesList.getDepartment) ||
    url.includes(Config.preSalesList.getVendorPO) ||
    url.includes(Config.preSalesList.saveVendorPayment) ||
    url.includes(Config.preSalesList.saveVendorPaymentRequest) ||
    url.includes(Config.preSalesList.checkProjectExist) ||
    url.includes(Config.preSalesList.approvalVendorPaymentRequest) ||
    url.includes(Config.preSalesList.vendorPaymentList) ||
    url.includes(Config.preSalesList.getVendorPaymentRequestList) ||
    url.includes(Config.preSalesList.vendorPaymentDetails) ||
    url.includes(Config.preSalesList.getVendorPaymentRequestDetail) ||
    url.includes(Config.preSalesList.invoiceDetails) ||
    url.includes(Config.preSalesList.downloadInvoicePdf) ||
    url.includes(Config.postSales.saveTutorialData) ||
    url.includes(Config.postSales.fetchTutorialData) ||
    url.includes(Config.postSales.valetIncentiveData) ||
    url.includes(Config.postSales.fetchIncentiveListing) ||
    url.includes(Config.postSales.editIncentiveData) ||
    url.includes(Config.postSales.lastFiveTransactionData) ||
    url.includes(Config.preSalesList.adjustAdvanceDetails) ||
    url.includes(Config.preSalesList.getSolutionQuestion) ||
    url.includes(Config.preSalesList.updateAdvanceRequest) ||
    url.includes(Config.preSalesList.updatePaymentRequest) ||
    url.includes(Config.preSalesList.updateAdjustmentRequest) ||
    url.includes(Config.preSalesList.contractList) ||
    url.includes(Config.preSalesList.approvalContractList) ||
    url.includes(Config.preSalesList.approvalAllContractList) ||
    url.includes(Config.preSalesList.approvalContractAptList) ||
    url.includes(Config.preSalesList.approvalContractAptAllList) ||
    url.includes(Config.preSalesList.approvalContractSupplyList) ||
    url.includes(Config.preSalesList.inspectionApprovalContractList) ||
    url.includes(Config.preSalesList.inspectionApprovalAptContractList) ||
    url.includes(Config.preSalesList.inspectionApprovalDetails) ||
    url.includes(Config.preSalesList.inspectionPostSolution) ||
    url.includes(Config.preSalesList.inspectionReject) ||
    url.includes(Config.preSalesList.bulkAssignment) ||
    url.includes(Config.brandActivation.postCampTraining) ||
    url.includes(Config.brandActivation.postPreCampAns) ||
    url.includes(Config.brandActivation.postCampTraining) ||
    url.includes(Config.brandActivation.campApprove) ||
    url.includes(Config.brandActivation.campReject) ||
    url.includes(Config.brandActivation.baReportees) ||
    url.includes(Config.brandActivation.baMaintenanceReportees) ||
    url.includes(Config.brandActivation.baChangeAssignee) ||
    url.includes(Config.brandActivation.smAssign) ||
    url.includes(Config.brandActivation.campApprovalList) ||
    url.includes(Config.maintenance.goLiveFilterDropDown) ||
    url.includes(Config.maintenance.pmSitesFilters) ||
    url.includes(Config.maintenance.pmTicketsFilters) ||
    url.includes(Config.maintenance.rmTicketsFilters) ||
    url.includes(Config.maintenance.rmSoftwareTicketsFilters) ||
    url.includes(Config.maintenance.rmHardwareTicketsFilters) ||
    url.includes(Config.maintenance.emSitesFilters) ||
    url.includes(Config.installation.mrfViewGateWise) ||
    url.includes(Config.installation.editMrf) ||
    url.includes(Config.installation.deleteMrf) ||
    url.includes(Config.categoryDetials.sampleDownload) ||
    url.includes(Config.maintenance.goLiveChangeAssignee) ||
    url.includes(Config.maintenance.materialInspected) ||
    url.includes(Config.maintenance.fetchMaterials) ||
    url.includes(Config.maintenance.saveMaterialInspection) ||
    url.includes(Config.maintenance.fetchMaterialInspection) ||
    url.includes(Config.maintenance.fetchPmDashboard) ||
    url.includes(Config.maintenance.fetchSitesAndGates) ||
    url.includes(Config.maintenance.fetchCategoryList) ||
    url.includes(Config.installation.locationChange) ||
    url.includes(Config.installation.checkChange) ||
    url.includes(Config.client.fetchBillingAddress) ||
    url.includes(Config.postSales.apartmentContractData) ||
    url.includes(Config.postSales.contractRevenueType) ||
    url.includes(Config.postSales.editApartmentContract) ||
    url.includes(Config.postSales.editSupplyContract) ||
    url.includes(Config.postSales.postInvoiceNumber) ||
    url.includes(Config.postSales.corpContractData) ||
    url.includes(Config.postSales.editCorpContract) ||
    url.includes(Config.brandActivation.getInfraId) ||
    url.includes(Config.brandActivation.saveCarnival) ||
    url.includes(Config.brandActivation.saveNormalCarnival) ||
    url.includes(Config.brandActivation.normalCarnivalListing) ||
    url.includes(Config.brandActivation.updateNormalCarnival) ||
    url.includes(Config.brandActivation.getNormalCarnival) ||
    url.includes(Config.fileUpload.docUploadPublic) ||
    url.includes(Config.brandActivation.carnivalListing) ||
    url.includes(Config.brandActivation.viewCarnival) ||
    url.includes(Config.brandActivation.getEmailClient) ||
    url.includes(Config.brandActivation.meetingMomSend) ||
    url.includes(Config.brandActivation.getMomList) ||
    url.includes(Config.brandActivation.getprojectOnboardingList) ||
    url.includes(Config.brandActivation.getSolution) ||
    url.includes(Config.brandActivation.getMaterialPrice) ||
    url.includes(Config.brandActivation.setQuotation) ||
    url.includes(Config.brandActivation.getAllQuotations) ||
    url.includes(Config.brandActivation.getQuotation) ||
    url.includes(Config.brandActivation.quotationStatusChange) ||
    url.includes(Config.brandActivation.quotationHistory) ||
    url.includes(Config.brandActivation.getCarnivalData) ||
    url.includes(Config.brandActivation.createDuplicateCarnival) ||
    url.includes(Config.brandActivation.updateCarnivalStatus) ||
    url.includes(Config.fileUpload.carnivalDocumentPublic) ||
    url.includes(Config.brandActivation.updateCarnivalData) ||
    url.includes(Config.fileComplain.listFdTickets) ||
    url.includes(Config.fileComplain.createFdTickets) ||
    url.includes(Config.fileComplain.viewFdTicketDetails) ||
    url.includes(Config.installation.getSiteInfoChecklist) ||
    url.includes(Config.valet.addValet) ||
    url.includes(Config.valet.project) ||
    url.includes(Config.valet.incentiveSlab) ||
    url.includes(Config.valet.updateValetStatus) ||
    url.includes(Config.valet.deleteSupervisor) ||
    url.includes(Config.valet.zoneList) ||
    url.includes(Config.valet.updateProject) ||
    url.includes(Config.valet.projectPricing) ||
    url.includes(Config.valet.getInfraId) ||
    url.includes(Config.valet.getMonthlyData) ||
    url.includes(Config.valet.dailySalaryDistribution) ||
    url.includes(Config.valet.calculateValetSalary) ||
    url.includes(Config.valet.markHolidays) ||
    url.includes(Config.valet.getAllRaisedTicketsForValet) ||
    url.includes(Config.valet.respondedByValetOps) ||
    url.includes(Config.valet.statusUpdateByValetHead) ||
    url.includes(Config.valet.updateSalaryStatus) ||
    url.includes(Config.valet.releaseSalary) ||
    url.includes(Config.valet.infraZone) ||
    url.includes(Config.valet.supervisor) ||
    url.includes(Config.valet.supervisorList) ||
    url.includes(Config.valet.valetList) ||
    url.includes(Config.valet.getValetListData) ||
    url.includes(Config.valet.updateValetListData) ||
    url.includes(Config.valet.updateValetSalary) ||
    url.includes(Config.valet.getSupervisorListData) ||
    url.includes(Config.valet.updateSupervisorListData) ||
    url.includes(Config.valet.searchProject) ||
    url.includes(Config.preSalesList.leadApprovalList) ||
    url.includes(Config.approvals.acceptLead) ||
    url.includes(Config.approvals.rejectLead) ||
    url.includes(Config.approvals.saveContractLogs) ||
    url.includes(Config.postSales.supplyContractData) ||
    url.includes(Config.distanceCalculator.siteData) ||
    url.includes(Config.distanceCalculator.calculateDistance) ||
    url.includes(Config.distanceCalculator.distanceCalculateData) ||
    url.includes(Config.distanceCalculator.distanceCalculateDataAtt) ||
    url.includes(Config.BulkAssignment.assignedTo) ||
    url.includes(Config.BulkAssignment.repotee) ||
    url.includes(Config.BulkAssignment.search) ||
    url.includes(Config.BulkAssignment.newAssignee) ||
    url.includes(Config.BulkAssignment.uploadcsvfile) ||
    url.includes(Config.networkEngineer.createTask) ||
    url.includes(Config.siteVisit.createTask) ||
    url.includes(Config.networkEngineer.taskList) ||
    url.includes(Config.siteVisit.taskList) ||
    url.includes(Config.siteVisit.getById) ||
    url.includes(Config.installation.getCitiesList) ||
    url.includes(Config.installation.getSitesByCityId) ||
    url.includes(Config.installation.getSubHardwareDropDown) ||
    url.includes(Config.postSales.getSalesIncentiveSites) ||
    url.includes(Config.postSales.getLeadCommertialDetails) ||
    url.includes(Config.postSales.saveSalesIncentive) ||
    url.includes(Config.postSales.getAllIncentives) ||
    url.includes(Config.postSales.getSeniorBeneficiary) ||
    url.includes(Config.postSales.getEditIncentiveData) ||
    url.includes(Config.postSales.approveOrRejectIncentive) ||
    url.includes(Config.postSales.getIncentiveListFinance) ||
    url.includes(Config.postSales.acceptIncetiveApproval) ||
    url.includes(Config.postSales.rejectIncentiveApproval) ||
    url.includes(Config.postSales.getUserSeniorIncentiveLists) ||
    url.includes(Config.postSales.excelExportIncentive) ||
    url.includes(Config.postSales.uploadUserSeniorIncentiveUtr) ||
    url.includes(Config.postSales.getHeadBeneficiary) || 
    url.includes(Config.postSales.getCalculatedIncentiveAmount) ||
    url.includes(Config.brandActivation.getContractDataForRenewal)||
    url.includes(Config.brandActivation.getRenewalApprovalList)||
    url.includes(Config.brandActivation.acceptRenewalApproval)||
    url.includes(Config.brandActivation.getInvoiceDetails)||
    url.includes(Config.brandActivation.rejectRenewalApproval)||
    url.includes(Config.brandActivation.renewalSaveData) ||
    url.includes(Config.brandActivation.getRenewalListing) ||
    url.includes(Config.profileUrl.getDashboardCount) ||
    url.includes(Config.materialRequistionForm.getSiteDetails) ||
    url.includes(Config.materialRequistionForm.siteDetailsBySiteNo) ||
    url.includes(Config.materialRequistionForm.material) ||
    url.includes(Config.materialRequistionForm.associateMaterial) ||
    url.includes(Config.materialRequistionForm.save) ||
    url.includes(Config.materialRequistionForm.materialTypes) ||
    url.includes(Config.materialRequistionForm.reportee) ||
    url.includes(Config.materialRequistionForm.warehouse) ||
    url.includes(Config.materialRequistionForm.pendingMaterial) ||
    url.includes(Config.materialRequistionForm.fetchSalesOrderReturnMrf) ||
    url.includes(Config.materialRequistionForm.sendSalesReturnToErp) ||
    url.includes(Config.materialRequistionForm.ViewMaterial) ||
    url.includes(Config.materialRequistionForm.approval) ||
    url.includes(Config.postSales.getSolutionStages) ||
    url.includes(Config.preSalesList.getSubLob) ||
    url.includes(Config.preSalesList.getVendorDepartments) ||
    url.includes(Config.preSalesList.addVendorAdvance) ||
    url.includes(Config.installation.getSiteRepositoryFinance)||
    url.includes(Config.brandActivation.renewalSaveEditData) ||
    url.includes(Config.viewDetails.discardContract) ||
    url.includes(Config.categoryDetials.supplyPocDropDown) ||
    url.includes(Config.maintenance.rmCheckOut) ||
    url.includes(Config.preSalesList.approveHoldRequest) ||
    url.includes(Config.preSalesList.vendorEditRequest) ||
    url.includes(Config.profileUrl.saveLocation) ||
    url.includes(Config.profileUrl.userById) ||
    url.includes(Config.materialRequistionForm.creditLimitListing) ||
    url.includes(Config.materialRequistionForm.toEmployee) ||
    url.includes(Config.materialRequistionForm.storeAddress) ||
    url.includes(Config.brandActivation.saveHelpdesk) ||
    url.includes(Config.brandActivation.viewHelpdesk) ||
    url.includes(Config.brandActivation.getHelpdeskData) ||
    url.includes(Config.brandActivation.updateHelpdeskData) ||
    url.includes(Config.brandActivation.updateHelpdeskStatus) ||
    url.includes(Config.brandActivation.createDuplicateHelpdesk) ||
    url.includes(Config.brandActivation.helpdeskListing) ||
    url.includes(Config.maintenance.rmUpdateRca) ||
    url.includes(Config.postSales.getUnclaimedList) ||
    url.includes(Config.postSales.verifyInvoiceNumbers) ||
    url.includes(Config.postSales.editClientDetails) ||
    url.includes(Config.maintenance.rmUpdateRca) ||
    url.includes(Config.fileComplain.getComplains) ||
    url.includes(Config.fileComplain.updateComplain) ||
    url.includes(Config.fileComplain.getComplains) ||
    url.includes(Config.fileComplain.updateComplain) ||
    url.includes(Config.preSalesList.taxInvoiceListingLob) ||
    url.includes(Config.preSalesList.bulkChangeAssigneeForTaxInvoices) ||
    url.includes(Config.installation.firstInteractionChecklist) ||
    url.includes(Config.installation.saveFirstInteractionCheckList) ||
    url.includes(Config.installation.configurationAndTestingChecklist) ||
    url.includes(Config.installation.saveConfigurationAndTesting)||
    url.includes(Config.preSalesList.getPaymentRequestData) ||
    url.includes(Config.maintenance.removeRcaItem) ||
    url.includes(Config.wallet.list) ||
    url.includes(Config.wallet.requestInternalWalletListing) ||
    url.includes(Config.wallet.walletRequestList) ||
    url.includes(Config.wallet.transaction) ||
    url.includes(Config.wallet.approvalList) ||
    url.includes(Config.wallet.addMoney) ||
    url.includes(Config.wallet.walletFinanceRequest) ||
    url.includes(Config.preSalesList.bulkChangeAssigneeForTaxInvoices) ||
    url.includes(Config.wallet.approveOrRejectWalletTransaction) ||
    url.includes(Config.wallet.requestApprovalList) ||
    url.includes(Config.wallet.downlaodWalletTransaction) ||
    url.includes(Config.wallet.viewRequest) ||
    url.includes(Config.wallet.requesttransactionList) ||
    url.includes(Config.preSalesList.tagMismatchApprovalList) ||
    url.includes(Config.preSalesList.tagApprovalDetails) ||
    url.includes(Config.preSalesList.tagMismatchApproval) ||
    url.includes(Config.preSalesList.tagMismatchReject) ||
    url.includes(Config.postSales.saveTransactionClaim) ||
    url.includes(Config.postSales.getClaimData) ||
    url.includes(Config.erp.erpInventory) ||
    url.includes(Config.maintenance.rmUpdateRca) ||
    url.includes(Config.maintenance.fetchHotoList) ||
    url.includes(Config.maintenance.getHotoAssigneeList) ||
    url.includes(Config.maintenance.fetchHotoEngList) ||
    url.includes(Config.maintenance.getHotoMaterialList) ||
    url.includes(Config.maintenance.saveHotoMaintenance) ||
    url.includes(Config.maintenance.getHotoDetails) ||
    url.includes(Config.maintenance.updateHotoStatus) ||
    url.includes(Config.maintenance.storeHotoInstallation) ||
    url.includes(Config.maintenance.updateHotoSiteAssignee) ||
    url.includes(Config.maintenance.getAllHotoDetails) ||
    url.includes(Config.brandActivation.getBusinessList) ||
    url.includes(Config.brandActivation.baOpportunityListFromLeadx) ||
    url.includes(Config.brandActivation.helperDetails.listing) ||
    url.includes(Config.brandActivation.helperDetails.getById) ||
    url.includes(Config.brandActivation.helperDetails.add) ||
    url.includes(Config.installation.getSiteIssues) ||
    url.includes(Config.installation.saveSiteIssue) ||
    url.includes(Config.materialRequistionForm.empMrfListing) ||
    url.includes(Config.materialRequistionForm.empMrfMaterial) ||
    url.includes(Config.materialRequistionForm.saveEmpMrfSerialNo) ||
    url.includes(Config.materialRequistionForm.mrfTypeDropdown) ||
    url.includes(Config.materialRequistionForm.showMrfView) ||
    url.includes(Config.materialRequistionForm.userCreditLimit) ||
    url.includes(Config.materialRequistionForm.getUserCreditLimit) ||
    url.includes(Config.materialRequistionForm.fetchMrfListing) ||
    url.includes(Config.installation.saveSiteIssue) ||
    url.includes(Config.postSales.getAllClientsForFinanceBusiness) ||
    url.includes(Config.postSales.unclaimedSendToModification) ||
    url.includes(Config.postSales.contractCategoryService) ||
    url.includes(Config.postSales.b2bInvoiceList) ||
    url.includes(Config.postSales.variableValetContractInvoice) ||
    url.includes(Config.postSales.capexContractInvoice) ||
    url.includes(Config.postSales.updateContractInvoice) ||
    url.includes(Config.postSalesNew.getOnDemandServices) ||
    url.includes(Config.postSales.valetServiceLineItemsUpdate) ||
    url.includes(Config.postSales.valetServiceLineItemsApprovalList) ||
    url.includes(Config.postSales.valetServiceLineItemsByInvoiceId) ||
    url.includes(Config.postSales.valetServiceLineItemsApprovalRejection) ||
    url.includes(Config.postSales.convertServiceInvoice) ||
    url.includes(Config.createLead.financeProjectOnboarding) ||
    url.includes(Config.businessAndClientLocation.addLocation) ||
    url.includes(Config.businessAndClientLocation.getClientList) ||
    url.includes(Config.brandActivation.getFlatFiles) ||
    url.includes(Config.materialRequistionForm.getChallans) ||
    url.includes(Config.materialRequistionForm.getChallanItems) ||
    url.includes(Config.materialRequistionForm.saveMaterialReceive) ||
    url.includes(Config.brandActivation.savePoc) ||
    url.includes(Config.installation.createProjectConfig) ||
    url.includes(Config.businessAndClientLocation.getClientList) ||
    url.includes(Config.businessAndClientLocation.checkGstNumber) ||
    url.includes(Config.installation.getProjectConfig) ||
    url.includes(Config.installation.siteListForEngineer) ||
    url.includes(Config.tagInfo.tagSearch) ||
    url.includes(Config.valet.tagCreation) ||
    url.includes(Config.valet.tagList) ||
    url.includes(Config.installation.getProjectConfig) ||
    url.includes(Config.brandActivation.getInvoicesMappedToSite) ||
    url.includes(Config.brandActivation.getChequePickedList) ||
    url.includes(Config.brandActivation.dispatchCheque) ||
    url.includes(Config.brandActivation.getTransactionAmountFromUncliamed) ||
    url.includes(Config.installation.siteListingForAsset) ||
    url.includes(Config.installation.getgatesByleadId) ||
    url.includes(Config.installation.getParentMaterials) ||
    url.includes(Config.installation.getAssetsAndMakeData) ||
    url.includes(Config.installation.saveSitesAssetsGateWise) ||
    url.includes(Config.installation.getAssetsInfoData) ||
    url.includes(Config.installation.markCompleteAssetMapping) ||
    url.includes(Config.installation.getgatesByContratctId) ||
    url.includes(Config.installation.getParentMaterialsBySiteId) ||
    url.includes(Config.installation.getAssetsInfoData) ||
    url.includes(Config.contractAssetsMapping.contractList) ||
    url.includes(Config.contractAssetsMapping.gateList) ||
    url.includes(Config.contractAssetsMapping.materialList) ||
    url.includes(Config.contractAssetsMapping.barcodeHistory) ||
    url.includes(Config.contractAssetsMapping.siteAssetsVerification) ||
    url.includes(Config.installation.markCompleteAssetMappingSite) ||
    url.includes(Config.preSalesList.invoiceModificationRequest) ||
    url.includes(Config.installation.markCompleteAssetMappingSite)||
    url.includes(Config.installation.getProjectConfig) || 
    url.includes(Config.viewDetails.viewSiteDetails) ||
    url.includes(Config.viewDetails.mapProject) ||
    url.includes(Config.preSalesList.returnTagMrf) ||
    url.includes(Config.installation.getClientAddressForContractMapping) ||
    url.includes(Config.installation.mapClientAddressToContract) ||
    url.includes(Config.installation.mapMasterClientAddressToContract) ||
    url.includes(Config.installation.firFormSubmit) ||
    url.includes(Config.installation.changeSiteStateToExpired) ||
    url.includes(Config.installation.changeSiteStateEnableDispute) ||
    url.includes(Config.installation.getSiteAbortApprovalFinance) ||
    url.includes(Config.installation.getapproveRejectDataFinance) ||
    url.includes(Config.installation.sitePosReplaceAndReturn) ||
    url.includes(Config.brandActivation.getClientAddressDetails) ||
    url.includes(Config.brandActivation.getBusinessAddressDetails) ||
    url.includes(Config.installation.saveasDraft) ||
    url.includes(Config.tagInfo.auditLogs) ||
    url.includes(Config.tagInfo.vehicleDetails) ||
    url.includes(Config.assesment.levelsByRoleId) || 
    url.includes(Config.assesment.getResources) ||
    url.includes(Config.assesment.questionsByRoleAndLevel) ||
    url.includes(Config.assesment.submitAssessmentTest) ||
    url.includes(Config.assesment.getAssessmentHistory) ||
    url.includes(Config.assesment.getMasterAssessmentHistory) ||
    url.includes(Config.helpdeskUser.userHelpdeskListing) ||
    url.includes(Config.helpdeskUser.saveUserConfiguration) ||
    url.includes(Config.helpdeskUser.userConfigListing) ||
    url.includes(Config.helpdeskUser.userConfigurationStatus) ||
    url.includes(Config.helpdeskUser.getAutofillUserData) ||
    url.includes(Config.helpdeskUser.backToModificationList) ||
    url.includes(Config.helpdeskUser.csvDownload) ||
    url.includes(Config.helpdeskUser.userDropdown) ||
    url.includes(Config.helpdeskUser.saveUserConfig) ||
    url.includes(Config.helpdeskUser.updateUserConfig) ||
    url.includes(Config.helpdeskUser.getAutofillData) ||
    url.includes(Config.helpdeskUser.userConfigStatus) ||
    url.includes(Config.helpdeskUser.userRequest) ||
    url.includes(Config.helpdeskUser.useCases) ||
    url.includes(Config.helpdeskUser.userRequestList) ||
    url.includes(Config.helpdeskUser.approvalPendingList) ||
    url.includes(Config.helpdeskUser.financeHierarchyList) ||
    url.includes(Config.helpdeskUser.RequestById) ||
    url.includes(Config.helpdeskUser.Approval) ||
    url.includes(Config.helpdeskUser.backToModification) ||
    url.includes(Config.helpdeskUser.backToModificationResponse) ||
    url.includes(Config.helpdeskUser.requestFilterData) ||
    url.includes(Config.helpdeskUser.approvalDropdown) ||
    url.includes(Config.fileComplain.fetchComplainSupport) || 
    url.includes(Config.fileComplain.saveComplaintReplySupport) ||
    url.includes(Config.fileComplain.saveIssueReply) ||
    url.includes(Config.fileComplain.getCategoryCX) ||
    url.includes(Config.fileComplain.setCategoryCX) ||
    url.includes(Config.fileComplain.centralTechComplain) ||
    url.includes(Config.fileComplain.centralTechComplainList) ||
    url.includes(Config.fileComplain.centralTechList) ||
    url.includes(Config.fileComplain.changeCentralTech) ||
    url.includes(Config.wallet.fecthDealerData) ||
    url.includes(Config.wallet.addPaymentForDealer) ||
    url.includes(Config.wallet.listDealerRequest) ||
    url.includes(Config.wallet.listDealerRequestApproval) ||
    url.includes(Config.wallet.walletApprovalFinance) ||
    url.includes(Config.wallet.approveOrReject) ||
    url.includes(Config.ledgerBusiness.ledgerList) ||
    url.includes(Config.invoiceValidation.list) ||
    url.includes(Config.invoiceValidation.closeRequest) ||
    url.includes(Config.ledgerBusiness.ledgerDownload) ||
    url.includes(Config.installation.getPosRequirement) ||
    url.includes(Config.installation.statusUpdatePos) ||
    url.includes(Config.brandActivation.uploadMeetingPlan) ||
    url.includes(Config.brandActivation.fetchUploadMeetingPlan) ||
    url.includes(Config.brandActivation.planListingDetails) ||
    url.includes(Config.maintenance.fetchMaintenanceSites) ||
    url.includes(Config.maintenance.fetchPrList) ||
    url.includes(Config.maintenance.fetchServicesAgainstPr) ||
    url.includes(Config.maintenance.saveServiceQuanity) ||
    url.includes(Config.approvals.rejectOrApprovePr) ||
    url.includes(Config.maintenance.getServiceQuantityConfirmation) ||
    url.includes(Config.maintenance.fetchPrListPoGenerated) ||
    url.includes(Config.maintenance.getPrConfirmationDetails) ||
    url.includes(Config.installation.getPosShipmentAndReasons) ||
    url.includes(Config.installation.fetchAmountAgainstPos) ||
    url.includes(Config.installation.replaceOrReturnPos) ||
    url.includes(Config.ledgerBusiness.ledgerDownload) ||
    url.includes(Config.installation.valetGoLiveChecklist) ||
    url.includes(Config.installation.postValetGoLive) ||
    url.includes(Config.installation.saveDashboardTraining) ||
    url.includes(Config.installation.getDashboardTrainingDetails) ||
    url.includes(Config.installation.getSiteCarnivalChecklist) ||
    url.includes(Config.installation.postCarnivalData) || 
    url.includes(Config.installation.getCarnivalCompletedDetails) ||
    url.includes(Config.installation.saveDashboardTrainingChecklist) ||
    url.includes(Config.installation.getDashboardTrainingChecklist) ||
    url.includes(Config.installation.fetchPosDevicesForEmp) ||
    url.includes(Config.maintenance.viewRmListForSlotChange) ||
    url.includes(Config.maintenance.ticketChangeSlot) ||
    url.includes(Config.maintenance.fetchPmSlotDetails) ||
    url.includes(Config.rcTransfer.getRcTransferList) ||
    url.includes(Config.rcTransfer.statusUpdateRcTransfer) ||
    url.includes(Config.maintenance.fetchPmSlotDetails) ||
    url.includes(Config.postSalesNew.createPostNew) ||
    url.includes(Config.postSalesNew.getPostNew) ||
    url.includes(Config.postSalesNew.createSolutionProduct)||
    url.includes(Config.postSalesNew.createClientDataForComm) ||
    url.includes(Config.postSalesNew.saveAdvancePayments) ||
    url.includes(Config.postSalesNew.postSalesInvoiceModule) ||
    url.includes(Config.postSalesNew.postSalesMileStone) ||
    url.includes(Config.postSalesNew.postSalesInstallation) ||
    url.includes(Config.postSalesNew.postCapexInvoiceModule) ||
    url.includes(Config.postSalesNew.contractInvoicePreview) ||
    url.includes(Config.installation.dismantleSite) ||
    url.includes(Config.installation.dismantleMrf) ||
    url.includes(Config.installation.contractDate) ||
    url.includes(Config.installation.dismantleMrfBySiteId) ||
    url.includes(Config.installation.getDashboardTrainingChecklist) ||
    url.includes(Config.CarLoan.ucdList) ||
    url.includes(Config.CarLoan.newCase) ||
    url.includes(Config.CarLoan.b2cUpdateLoanApplication) ||
    url.includes(Config.CarLoan.b2cCaseLoginWithoutDealer) ||
    url.includes(Config.CarLoan.caseListing) ||
    url.includes(Config.CarLoan.caseById) ||
    url.includes(Config.CarLoan.carBrand) ||
    url.includes(Config.CarLoan.carModel) ||
    url.includes(Config.CarLoan.carVariant) ||
    url.includes(Config.CarLoan.carBrandForB2C) ||
    url.includes(Config.CarLoan.carModelForB2C) ||
    url.includes(Config.CarLoan.bankList) ||
    url.includes(Config.CarLoan.caseByLsqDealerId) ||
    url.includes(Config.CarLoan.updateCaseStatus) ||
    url.includes(Config.CarLoan.loanApplicationById) ||
    url.includes(Config.CarLoan.loanApplicationUpdate) ||
    url.includes(Config.CarLoan.raisePayment) ||
    url.includes(Config.CarLoan.loanAccount) ||
    url.includes(Config.CarLoan.loanUndertaking) ||
    url.includes(Config.CarLoan.loanTranches) ||
    url.includes(Config.CarLoan.loanPaymentRequest) ||
    url.includes(Config.CarLoan.updateTrancheStatus) ||
    url.includes(Config.CarLoan.raisePaymentUpdate) ||
    url.includes(Config.CarLoan.accountPennyDrop) ||
    url.includes(Config.CarLoan.bankSheetUpload) ||
    url.includes(Config.CarLoan.getThePayoutDetails) ||
    url.includes(Config.CarLoan.updateThePayoutDetails) ||
    url.includes(Config.CarLoan.getAccountDetailsForPayout) ||
    url.includes(Config.CarLoan.newBankDetails) ||
    url.includes(Config.CarLoan.payoutConfigurationList) ||
    url.includes(Config.CarLoan.payoutConfigurationVerify) ||
    url.includes(Config.CarLoan.foreClosureAccountDetails) ||
    url.includes(Config.CarLoan.vehicleDetails) ||
    url.includes(Config.CarLoan.rcCaseList) ||
    url.includes(Config.CarLoan.rcCaseById) ||
    url.includes(Config.CarLoan.commissionPayoutCasesList) ||
    url.includes(Config.CarLoan.invoiceValidateStatus) ||
    url.includes(Config.CarLoan.previewInvoice) ||
    url.includes(Config.CarLoan.raiseInvoiceWithRecon) ||
    url.includes(Config.CarLoan.rtoDetailsUpdate) ||
    url.includes(Config.CarLoan.approvalRequest) ||
    url.includes(Config.CarLoan.b2cLeadList) ||
    url.includes(Config.CarLoan.ucdView) ||
    url.includes(Config.imageCompression.compressImage)||
    url.includes(Config.installation.getInstallationDateChangeListing)||
    url.includes(Config.installation.viewInstallationDateChangeDetail)||
    url.includes(Config.installation.createInstallationDateChange)||
    url.includes(Config.installation.approveInstallationDateChange)||
    url.includes(Config.valet.getContacts)||
    url.includes(Config.valet.getProjects)||
    url.includes(Config.contractRenewal.getSiteList)||
    url.includes(Config.contractRenewal.getPrefilledGateData)||
    url.includes(Config.contractRenewal.saveRenewalRequest)||
    url.includes(Config.contractRenewal.getPrefilledRequest)||
    url.includes(Config.contractRenewal.getRequestList)||
    url.includes(Config.contractRenewal.updateRequestStatus)||
    url.includes(Config.contractRenewal.getRequestDetails)||
    url.includes(Config.valet.broadcastNotification) ||
    url.includes(Config.brandActivation.getNcsMeetings) ||
    url.includes(Config.brandActivation.getNcsMeetingbyId) ||
    url.includes(Config.brandActivation.saveAndCheckoutNCSMeeting) ||
    url.includes(Config.contractRenewal.getFilters)||
    url.includes(Config.contractRenewal.getPreviousRequests)||
    url.includes(Config.brandActivation.saveAndCheckInNCSMeeting) ||
    url.includes(Config.maintenance.generateHappyCode) ||
    url.includes(Config.maintenance.generateHappyCodePM) ||
    url.includes(Config.client.fetchBillingGST) ||
    url.includes(Config.tagInfo.tagSearchByVehicleNo)||
    url.includes(Config.contractRenewal.getGateBasePrices)||
    url.includes(Config.brandActivation.invoicesBySiteNumber) ||
    url.includes(Config.tickets.userRoleDropdown) || 
    url.includes(Config.tickets.saveExternalUser) ||
    url.includes(Config.tickets.externalUsersList) ||
    url.includes(Config.valet.getPenaltyList)||
    url.includes(Config.valet.masterSalaryPenalty)||
    url.includes(Config.valet.masterSalaryPenaltyLis)||
    url.includes(Config.valet.salaryByPhoneNo)||
    url.includes(Config.valet.userNotificationList)||
    url.includes(Config.valet.fetchAttendanceRequests)||
    url.includes(Config.valet.getAllDays)||
    url.includes(Config.valet.valetAttendanceRequest)||
    url.includes(Config.valet.allValetUsers)||
    url.includes(Config.valet.getEventSalary)||
    url.includes(Config.valet.approveEventSalary) ||
    url.includes(Config.valet.advanceSalaryList)||
    url.includes(Config.valet.advanceSalaryCalculate) ||
    url.includes(Config.valet.advanceSalaryUpdate) ||
    url.includes(Config.brandActivation.getClientOrdersList)||
    url.includes(Config.preSalesList.vendorInvoiceRequestDetails)||
    url.includes(Config.preSalesList.vendorInvoiceRequestList)||
    url.includes(Config.preSalesList.invoiceRequestDetails)||
    url.includes(Config.preSalesList.updateVendorPaymentStatus)||
    url.includes(Config.maintenance.fetchProjectPmSlotDetails)||
    url.includes(Config.maintenance.fetchMaintenancePMForProjects)||
    url.includes(Config.preSalesList.getSiteChallan) ||
    url.includes(Config.brandActivation.getClientOrdersList)||
    url.includes(Config.postSales.valetIncentiveBulkApproval)||
    url.includes(Config.contractRenewal.updateClientStatus)||
    url.includes(Config.contractRenewal.getClientRemarks)||
    url.includes(Config.fileComplain.saveCteComplainRemarks)||
    url.includes(Config.fileComplain.cteIssueListing)||
    url.includes(Config.preSalesSupply.getBlockedUserData)||
    url.includes(Config.preSalesSupply.updateUserBlockStatus) || 
    url.includes(Config.installation.requiredDocumentDropdown) || 
    url.includes(Config.installation.saveCompetitionMapping) || 
    url.includes(Config.installation.competitionMappingList) || 
    url.includes(Config.installation.fetchSiteNameUsingLatLong) || 
    url.includes(Config.installation.insertDocumentsStageData) ||
    url.includes(Config.installation.getSiteConsumablesItems) ||
    url.includes(Config.installation.updateSiteConsumesByOps) ||
    url.includes(Config.postSalesNew.getMasterInventory) ||
    url.includes(Config.client.getClientByPan)||
    url.includes(Config.creditPolicy.getCredits)||
    url.includes(Config.creditPolicy.updateBusinessCredits)||
    url.includes(Config.creditPolicy.updateEmployeeCredits)||
    url.includes(Config.creditPolicy.retagInvoice)||
    url.includes(Config.creditPolicy.getEmployeeInvoiceListing)||
    url.includes(Config.creditPolicy.getEmployeeTeamList)||
    url.includes(Config.client.getDetailsByGst) ||
    url.includes(Config.offRoleEmployees.getOffRoleEmployeesList) ||
    url.includes(Config.offRoleEmployees.fetchDropdownDatas) ||
    url.includes(Config.offRoleEmployees.updateActionByBusinessHead) ||
    url.includes(Config.businessItems.createDeleteBrand)||
    url.includes(Config.businessItems.createDeleteCategory)||
    url.includes(Config.businessItems.getBrands)||
    url.includes(Config.businessItems.getCategories)||
    url.includes(Config.businessItems.createDeleteHSN)||
    url.includes(Config.businessItems.getHsnListing)||
    url.includes(Config.businessItems.getGstData)||
    url.includes(Config.businessItems.getItemDropdownData)||
    url.includes(Config.businessItems.fetchBrandAndHsn)||
    url.includes(Config.businessItems.getBusinessItemListing)||
    url.includes(Config.businessItems.createMaterials)||
    url.includes(Config.businessItems.editMaterials)||
    url.includes(Config.businessItems.approveItem)||
    url.includes(Config.lob.getLobListing)||
    url.includes(Config.lob.createLob)||
    url.includes(Config.lob.editLob)||
    url.includes(Config.viewDetails.projectMappingDetails) ||
    url.includes(Config.offRoleEmployees.generateOnboardingLink) || 
    url.includes(Config.offRoleEmployees.departmentDropdown) || 
    url.includes(Config.offRoleEmployees.teamDropdown) || 
    url.includes(Config.offRoleEmployees.roleDropdown) || 
    url.includes(Config.offRoleEmployees.reportingManagerDropdown) || 
    url.includes(Config.offRoleEmployees.citiesDropdown) || 
    url.includes(Config.offRoleEmployees.zonesDropdown) || 
    url.includes(Config.offRoleEmployees.assignLocations) || 
    url.includes(Config.offRoleEmployees.assignReportee) || 
    url.includes(Config.offRoleEmployees.areasDropdown) ||
    url.includes(Config.offRoleEmployees.fetchUserLocationMapping) || 
    url.includes(Config.offRoleEmployees.fetchUserTeamMapping) ||
    url.includes(Config.offRoleEmployees.deleteUserMapping) ||
    url.includes(Config.fastag.uploadPricingRequest) ||
    url.includes(Config.fastag.bundleApi) ||
    url.includes(Config.fastag.markinactive)||
    url.includes(Config.fastag.transferTag) ||
    url.includes(Config.fastag.addCashback) ||
    url.includes(Config.fastag.deleteCashback) ||
    url.includes(Config.fastag.reAssignUser) ||
    url.includes(Config.fastag.removeSbiReplacementTags) ||
    url.includes(Config.user.getUserDetailsByUserId) ||
    url.includes(Config.user.userDevices) ||
    url.includes(Config.user.reportingHierarchy) ||
    url.includes(Config.materialRequistionForm.getMrfTypeList)||
    url.includes(Config.materialRequistionForm.getMrfTypeDropdownData)||
    url.includes(Config.materialRequistionForm.createMrfType)||
    url.includes(Config.materialRequistionForm.editMrfType)||
    url.includes(Config.materialRequistionForm.deleteMrfType)||
    url.includes(Config.channelPartner.getChannelPartnerListing) ||
    url.includes(Config.channelPartner.transferHandoverToChannelPartner) ||
    url.includes(Config.channelPartner.fetchContractsByChannelPartner) ||
    url.includes(Config.channelPartner.invoiceVisibilityForChannelPartner)||
    url.includes(Config.fastag.getToaTagOrders)||
    url.includes(Config.fastag.approveTagOrder)||
    url.includes(Config.fastag.uploadTagOrderCsv)||
    url.includes(Config.installation.approvePRRequest)||
    url.includes(Config.installation.updatePRItems)||
    url.includes(Config.jms.jmsPoDetails)||
    url.includes(Config.jms.submitJmsDetails)||
    url.includes(Config.jms.approveJmsDetails)||
    url.includes(Config.jms.getJmsListing)||
    url.includes(Config.preSalesList.salesTargetsSetTargets) ||
    url.includes(Config.preSalesList.salesTargetsList) ||
    url.includes(Config.preSalesList.salesTargetsLatestPeriod) ||
    url.includes(Config.preSalesList.salesTargetsCloseAndCreateNext)||
    url.includes(Config.fastag.getUserAnalytics)||
    url.includes(Config.fastag.presignedUrlCreate)||
    url.includes(Config.fastag.presignedUrlComplete)||
    url.includes(Config.fastag.bulkUploadKyV)||
    url.includes(Config.fastag.bulkUploadListing)||
    url.includes(Config.fastag.validateBulkUploadRequest)||
    url.includes(Config.fastag.fseBulkMapping)||
    url.includes(Config.fastag.sbiAgentPortalData)||
    url.includes(Config.installation.updateVendorAllocation) ||
    url.includes(Config.dynamicContents.list) ||
    url.includes(Config.dynamicContents.view) ||
    url.includes(Config.dynamicContents.add) ||
    url.includes(Config.dynamicContents.edit) || 
    url.includes(Config.helpdeskUser.reconPiTiValidation) ||
    url.includes(Config.siteLeadReferral.list) ||
    url.includes(Config.siteLeadReferral.rejectReasons) ||
    url.includes(Config.siteLeadReferral.apiPath) ||
    url.includes(Config.siteLeadReferral.listSpoc) ||
    url.includes(Config.preSalesList.getSalesTargetsPeriods) ||
    url.includes(Config.preSalesList.salesTargetsSetWeeklyTargets) ||
    url.includes(Config.preSalesList.salesTargetsUpdatePeriod) ||
    url.includes(Config.attendance.checkUserLocation) ||
    url.includes(Config.attendance.checkAttendance) ||
    url.includes(Config.attendance.markDarwinAttendance) ||
    url.includes(Config.darwinAttendance.monthsFilter) ||
    url.includes(Config.darwinAttendance.monthlyAttendance) ||
    url.includes(Config.darwinAttendance.dailyMeetings) ||
    url.includes(Config.darwinAttendance.createRegularization) ||
    url.includes(Config.darwinAttendance.regularizationRequests) ||
    url.includes(Config.darwinAttendance.regularizationFilter) ||
    url.includes(Config.darwinAttendance.regularizationDetails) ||
    url.includes(Config.darwinAttendance.updateRegularization) ||
    url.includes(Config.preSalesList.salesTargetsUploadAdditionalAchievements);
export default axios;
